import React, { useState } from "react";
import { Button, Modal, TextField } from "../../../components/ui";

/**
 * What has to be typed to arm the button.
 *
 * Upper case, and matched exactly: a word that has to be copied letter for
 * letter cannot be got through by pressing Enter twice on a dialog somebody
 * thought said something else. It is the account, the client list, every plan
 * written for them and the whole revenue history — there is no undo behind this
 * and no support call that puts it back.
 */
const CONFIRM_WORD = "DELETE";

interface DeleteAccountModalProps {
  open: boolean;
  /** True while the deletion is with the server — the dialog stands down. */
  deleting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * The dialog behind Delete account: what goes, and the word that proves it was
 * meant.
 *
 * It states the losses as a list rather than a sentence. "Your account and all
 * its data" is the kind of phrase that gets skimmed; named things — the
 * clients, the plans, the revenue, the branding, the account itself — are what
 * make somebody stop and think about the one they had not considered.
 */
export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  open,
  deleting = false,
  onClose,
  onConfirm,
}) => {
  const [typed, setTyped] = useState("");

  const armed = typed.trim() === CONFIRM_WORD;

  const close = () => {
    // Reopening starts from an empty box: a dialog that remembers the word is
    // a dialog whose safeguard only works once.
    setTyped("");
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Delete your account"
      onClose={() => {
        if (!deleting) close();
      }}
      className="max-w-[580px]"
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            disabled={deleting}
            onClick={close}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            disabled={!armed}
            loading={deleting}
            onClick={onConfirm}
          >
            Delete account
          </Button>
        </>
      }
    >
      <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
        Deleting your account is permanent. You&rsquo;ll lose access to your
        workspace and all data associated with it, including:
      </p>
      <ul className="mt-2 list-disc pl-5 text-[14px] 3xl:text-[15px] leading-[1.7] text-ink-muted">
        <li>Your account and workspace</li>
        <li>All clients and enquiries</li>
        <li>All meal plans</li>
        <li>Revenue and invoice history</li>
        <li>Your saved branding details</li>
      </ul>
      <p className="mt-3 text-[14px] 3xl:text-[15px] font-medium text-ink">
        Once deleted, this data cannot be recovered.
      </p>

      <TextField
        label={`To permanently delete your account, type ${CONFIRM_WORD} below.`}
        className="mt-4"
        placeholder={CONFIRM_WORD}
        autoComplete="off"
        // Never guessed at for the user: the whole point is that it is typed.
        spellCheck={false}
        disabled={deleting}
        value={typed}
        onValueChange={setTyped}
      />
    </Modal>
  );
};

export default DeleteAccountModal;
